import { ChangeEvent } from "react";

import { Client } from "@/Common/Api/Client";
import { Diagnosis } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Diagnosis";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DiagnosisHelper } from "@/Patient/Helpers/DiagnosisHelper";
import { DiagnosisModel } from "@/Patient/Models/MedicalSummary/DiagnosisModel";

interface IDiagnosisSearchAutocomplete {
    onItemSelect: (item: Diagnosis) => void;
    onFavoriteItemSelect?: (item: DiagnosisModel) => void;
    onTextChange: (text: string) => void;

    onBlur?: (text: string) => void;
    clearTextAfterSelect?: boolean;

    debounce: number;
    disableFavorites?: boolean;
    newValue: string;
    valid?: boolean;
    id: string;
    dataCy?: string;
    siteId: string;
}

class DiagnosisSearchListItem {
    public isFavorite: boolean;
    public isActive: boolean;
    public id: string;
    public favoriteItem: DiagnosisModel;

    constructor(init?: Partial<DiagnosisSearchListItem>) {
        Object.assign(this, init);
    }
}

class DiagnosisSearchAutocompleteState {
    public forceHideList: boolean = false;
    public text: string = "";
    public cancelOnblur: boolean = false;
    public list: Immutable.List<Diagnosis> = Immutable.List([]);
    public favorites: DiagnosisSearchListItem[] = null;
    public openedByClick: boolean;

    constructor(fields?: Partial<DiagnosisSearchAutocompleteState>) {
        Object.assign(this, fields);
    }
}

class DiagnosisSearchAutocomplete extends BaseReactComponent<IDiagnosisSearchAutocomplete, DiagnosisSearchAutocompleteState> {
    protected input: HTMLInputElement;
    private timeoutReference: number;

    public constructor(props: IDiagnosisSearchAutocomplete) {
        super(props);
        this.timeoutReference = -1;

        this.state = new DiagnosisSearchAutocompleteState({
            forceHideList: false,
            text: props.newValue ?? "",
            list: Immutable.List([])
        });
    }

    public componentDidMount(): void {
        this.input?.focus?.();
    }

    public componentDidUpdate(prevProps: IDiagnosisSearchAutocomplete): void {
        if (prevProps.newValue !== this.props.newValue) {
            this.mutateState(s => s.text = this.props.newValue);
        }
    }

    public render(): JSX.Element {
        const siteModel = cacheStore.GetSiteById(this.props.siteId);

        return (
            <div className="fmp-autocomplete">
                <input
                    ref={ref => this.input = ref}
                    id={this.props.id}
                    data-cy={this.props.dataCy}
                    type="text"
                    value={this.state.text}
                    className={this.props.valid === false ? "form-control invalid" : "form-control"}
                    onBlur={() => this.onBlur()}
                    onFocus={() => this.onFocus()}
                    onKeyDown={e => this.inputKeyDown(e)}
                    onChange={e => this.onTextChange(e)}
                />
                {this.state.list.size > 0 && !this.state.forceHideList && (
                    <div className="list-container-txtbox">
                        <div
                            className="results-container"
                            data-cy={this.props.dataCy ? `${this.props.dataCy}-results` : null}
                            style={{ minWidth: "150px" }}
                            onMouseEnter={() => this.mutateState(s => s.cancelOnblur = true)}
                            onMouseLeave={() => this.mutateState(s => s.cancelOnblur = false)}
                        >
                            <ul className="results-list">
                                {this.state.list.map(
                                    (item: Diagnosis, i: number) => {
                                        const displayLocal = item.getLocaleFromSite(siteModel);
                                        const itemName = item.getName(displayLocal);

                                        const divStyle: React.CSSProperties = item.Active == true ? { backgroundColor: "#f4f6f9" } : {};
                                        const hyperlinkStyle: React.CSSProperties = item.Active == true ? { background: "lightblue" } : {};

                                        return (
                                            <li key={i + "__code_"} onClick={() => this.selectItem(item)}>
                                                <div className="flex-container" style={divStyle}>
                                                    <a style={hyperlinkStyle}>
                                                        <span>{`${item.Code} ${itemName} - `}</span>
                                                        <span style={{ fontStyle: "italic" }}>{DiagnosisHelper.getSourceNameFromCode(item.Type)}</span>
                                                    </a>
                                                </div>
                                            </li>
                                        );
                                    }
                                )}
                            </ul>

                        </div>
                    </div>
                )}
            </div>
        );
    }

    protected onTextChange(e: ChangeEvent<HTMLInputElement>): void {
        if (this.timeoutReference !== -1) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = -1;
        }

        const value = e.target.value;

        this.props.onTextChange?.(value);

        this.mutateState(
            s => {
                s.forceHideList = value == "";
                s.text = value;
            },
            () => value != "" && this.loadData(value)
        );
    }

    protected forceHideList(then?: () => void): void {
        this.mutateState(s => s.forceHideList = true, then);
    }

    protected inputKeyDown(e: React.KeyboardEvent): void {
        switch (e.keyCode) {
            // up
            case 38:
                e.preventDefault();
                this.moveSelection(true);
                break;

            // down
            case 40:
                e.preventDefault();
                this.moveSelection(false);
                break;

            // enter
            case 13:
                if (this.state.forceHideList == true || this.state.list == null || this.state.list.count() == 0)
                    this.forceHideList(() => this.loadData(this.state.text));
                else
                    this.addSelectedItem();
                break;

            // escape
            case 27:
                this.forceHideList();
                break;
        }
    }

    protected moveSelection(isDirectionUp: boolean): void {
        this.mutateState(s => {
            const { collection, forceHide } = this.moveSelectionUpOrDown(s.list.toArray(), isDirectionUp);
            s.forceHideList = forceHide;
            s.list = Immutable.List(collection);
        });
    }

    // keyboard triggered
    private moveSelectionUpOrDown(collection: Diagnosis[], isDirectionUp: boolean): { collection: Diagnosis[], forceHide: boolean } {
        if (collection.length == 0)
            return { collection, forceHide: false };

        let index = this.indexOfSelectedItem(collection);
        if (index === -1) {
            collection.forEach(x => x.Active = false);
            collection[isDirectionUp ? collection.length - 1 : 0].Active = true;

            return { collection, forceHide: false };
        }

        if ((index === 0 && isDirectionUp) || (index === collection.length - 1 && !isDirectionUp)) {
            collection[isDirectionUp ? 0 : collection.length - 1].Active = false;
            return { collection, forceHide: true };
        }

        collection[index].Active = false;
        index = index + (isDirectionUp ? -1 : 1);

        collection[index].Active = true;
        return { collection, forceHide: false };
    }

    private indexOfSelectedItem(list: Diagnosis[]): number {
        if (list.length === 0)
            return -1;

        for (let i = 0; i < list.length; i++) {
            let item = list[i];
            if (item.Active == true)
                return i;
        }
        return -1;
    }

    protected addSelectedItem(): void {
        const item = this.state.list.toArray().firstOrDefault(item => item.Active);

        if (item) {
            this.selectItem(item);
        }
    }

    protected loadData(text: string, noDebounce: boolean = false): void {
        this.timeoutReference = window.setTimeout(
            () => this.loadDataRaw(text),
            noDebounce ? 150 : this.props.debounce
        );
    }

    protected async loadDataRaw(text: string): Promise<void> {
        if (text?.trim()?.length < 2)
            return;

        const siteModel = cacheStore.GetSiteById(this.props.siteId);

        const response = await Client.postAsync<{ Diagnostics: Diagnosis[] }>(
            ServerOperations.Server.MedfarDictionnary_SearchDiagnostics,
            {
                SearchString: text,
                CountryLookupId: siteModel.getCountryLookupId(),
                StateLookupId: siteModel.getStateLookupId()
            }
        );

        if (!response.isSuccess) {
            console.error(response.errors);
            return;
        }

        this.mutateState(s => {
            const models = response.data?.Diagnostics?.map(p => new Diagnosis(p));
            if (models?.length > 0)
                models[0].Active = true;

            s.list = Immutable.List(models);
            if (this.props.id === document.activeElement.id)
                s.forceHideList = false;
        });
    }

    protected selectItem(item: Diagnosis): void {
        const siteModel = cacheStore.GetSiteById(this.props.siteId);
        const displayLocal = item.getLocaleFromSite(siteModel);

        this.mutateState(
            s => {
                s.forceHideList = true;
                s.list = Immutable.List([]);
                s.text = this.props.clearTextAfterSelect
                    ? ""
                    : `${item.Code} - ${item.getName(displayLocal)}`;
                s.cancelOnblur = true;
            },
            () => {
                // wait until state has been set to call parent select item callback
                this.props.onItemSelect?.(item);
                this.input?.focus?.();
                this.input?.select?.();

                window.setTimeout(() => this.mutateState(s => s.cancelOnblur = false), 200);
            }
        );
    }

    protected onFocus(): void {
        // reset hide result list state
        this.mutateState(s => {
            s.list = Immutable.List([]);
            s.forceHideList = false;
        });
    }

    protected onBlur(): void {
        if (!this.state.cancelOnblur) {
            let latestText = "";
            this.mutateState(
                s => {
                    s.list = Immutable.List([]);
                    s.forceHideList = true;

                    latestText = this.state.text;
                    if (this.props.clearTextAfterSelect)
                        s.text = "";
                },
                () => this.props.onBlur?.(latestText)
            );
        }
    }
}

export { DiagnosisSearchAutocomplete, DiagnosisSearchAutocompleteState, DiagnosisSearchListItem, IDiagnosisSearchAutocomplete };
